import { useCallback, useState } from 'react';

type State = {
  stack: 'images' | 'videos' | null;
  /** Where the lightbox is, whether or not it is the one showing. */
  imageIndex: number;
  /** Where the reel is, likewise. */
  videoIndex: number;
};

export type MediaViewer = {
  /** Which stack is showing, or null when neither is. */
  stack: State['stack'];
  imagesOpen: boolean;
  videosOpen: boolean;
  imageIndex: number;
  videoIndex: number;
  openImages: (index: number) => void;
  openVideos: (index: number) => void;
  close: () => void;
  setImageIndex: (index: number) => void;
  setVideoIndex: (index: number) => void;
};

/**
 * Which viewer is open, and where each one is.
 *
 * One hook for both viewers rather than two pieces of state, because the two
 * are mutually exclusive and saying so once is cheaper than keeping two
 * booleans honest. A tile does not know or care which viewer its own medium
 * belongs in: it calls `openImages` or `openVideos` with a position in that
 * stack, and the stacks were separated by the data layer long before this.
 *
 * ## One index per stack, not one shared index
 *
 * This looked like duplication and is not. The two viewers are mounted at the
 * same time — a closed MUI `Dialog` still renders its component, it just draws
 * nothing — so both are live, both clamp their index against their own list,
 * and with a shared index the closed one wins: filter the gallery to videos,
 * and the lightbox's stack is empty, so its clamp pins the shared index at 0
 * and the reel opens on the first clip no matter which one was tapped. Two
 * indices cannot cross-talk.
 *
 * The index also survives closing on purpose. Somebody who opens the reel,
 * swipes to the fourth clip, closes it to read the grid and opens it again
 * lands where they left off — and the tile they tap simply overrides it.
 */
export function useMediaViewer(): MediaViewer {
  const [state, setState] = useState<State>({ stack: null, imageIndex: 0, videoIndex: 0 });

  const openImages = useCallback(
    (index: number) => setState((prev) => ({ ...prev, stack: 'images', imageIndex: index })),
    [],
  );
  const openVideos = useCallback(
    (index: number) => setState((prev) => ({ ...prev, stack: 'videos', videoIndex: index })),
    [],
  );
  const close = useCallback(() => setState((prev) => ({ ...prev, stack: null })), []);
  const setImageIndex = useCallback(
    (index: number) => setState((prev) => ({ ...prev, imageIndex: index })),
    [],
  );
  const setVideoIndex = useCallback(
    (index: number) => setState((prev) => ({ ...prev, videoIndex: index })),
    [],
  );

  return {
    stack: state.stack,
    imagesOpen: state.stack === 'images',
    videosOpen: state.stack === 'videos',
    imageIndex: state.imageIndex,
    videoIndex: state.videoIndex,
    openImages,
    openVideos,
    close,
    setImageIndex,
    setVideoIndex,
  };
}
